import { KnowledgeNode } from '../../types/knowledge';
import { Conversation, Message } from '../../types/conversation';
import { User } from '../../types/user';

export interface SyncOperation {
  id: string;
  type: 'create' | 'update' | 'delete';
  resourceType: 'knowledge' | 'conversation' | 'user' | 'integration';
  resourceId: string;
  data: any;
  previousData?: any;
  timestamp: string;
  priority: number;
  retryCount: number;
  status: 'pending' | 'syncing' | 'completed' | 'failed' | 'conflict';
  errorMessage?: string;
  dependsOn?: string[];
}

export interface ConflictRecord {
  id: string;
  resourceType: string;
  resourceId: string;
  localVersion: any;
  remoteVersion: any;
  conflictType: 'edit' | 'delete' | 'permission' | 'schema';
  timestamp: string;
  status: 'pending' | 'resolved' | 'ignored';
  resolution?: ConflictResolution;
  autoResolvable: boolean;
}

export interface ConflictResolution {
  strategy: 'local' | 'remote' | 'merge' | 'manual';
  mergedData?: any;
  userChoice?: 'local' | 'remote';
  timestamp: string;
}

export interface CacheMetadata {
  key: string;
  cachedAt: string;
  expiresAt?: string;
  size: number;
  accessCount: number;
  lastAccessed: string;
}

export interface OfflineQueryFilters {
  type?: string;
  userId?: string;
  organizationId?: string;
  dateRange?: {
    start: string;
    end: string;
  };
  tags?: string[];
  searchTerm?: string;
  limit?: number;
  offset?: number;
}

export class OfflineDBManager {
  private db: IDBDatabase | null = null;
  private dbName = 'PrismaticForgeOffline';
  private dbVersion = 3;

  async initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        this.createObjectStores(db, event.oldVersion);
      };
    });
  }

  private createObjectStores(db: IDBDatabase, oldVersion: number): void {
    // Knowledge nodes store
    if (!db.objectStoreNames.contains('knowledge')) {
      const knowledgeStore = db.createObjectStore('knowledge', { keyPath: 'id' });
      knowledgeStore.createIndex('type', 'type', { unique: false });
      knowledgeStore.createIndex('lastModified', 'lastModified', { unique: false });
      knowledgeStore.createIndex('userId', 'userId', { unique: false });
      knowledgeStore.createIndex('organizationId', 'organizationId', { unique: false });
      knowledgeStore.createIndex('tags', 'tags', { unique: false, multiEntry: true });
      knowledgeStore.createIndex('offlineModified', 'offlineModified', { unique: false });
    }

    // Conversations store
    if (!db.objectStoreNames.contains('conversations')) {
      const conversationStore = db.createObjectStore('conversations', { keyPath: 'id' });
      conversationStore.createIndex('participants', 'participants', { unique: false, multiEntry: true });
      conversationStore.createIndex('lastActivity', 'lastActivity', { unique: false });
      conversationStore.createIndex('userId', 'userId', { unique: false });
      conversationStore.createIndex('offlineModified', 'offlineModified', { unique: false });
    }

    // Messages store
    if (!db.objectStoreNames.contains('messages')) {
      const messageStore = db.createObjectStore('messages', { keyPath: 'id' });
      messageStore.createIndex('conversationId', 'conversationId', { unique: false });
      messageStore.createIndex('timestamp', 'timestamp', { unique: false });
      messageStore.createIndex('userId', 'userId', { unique: false });
    }

    // Users store
    if (!db.objectStoreNames.contains('users')) {
      const userStore = db.createObjectStore('users', { keyPath: 'id' });
      userStore.createIndex('email', 'email', { unique: true });
      userStore.createIndex('organizationId', 'organizationId', { unique: false });
    }

    // Sync queue store
    if (!db.objectStoreNames.contains('sync_queue')) {
      const syncStore = db.createObjectStore('sync_queue', { keyPath: 'id' });
      syncStore.createIndex('timestamp', 'timestamp', { unique: false });
      syncStore.createIndex('priority', 'priority', { unique: false });
      syncStore.createIndex('status', 'status', { unique: false });
      syncStore.createIndex('resourceType', 'resourceType', { unique: false });
      syncStore.createIndex('retryCount', 'retryCount', { unique: false });
    }

    // Conflicts store
    if (!db.objectStoreNames.contains('conflicts')) {
      const conflictStore = db.createObjectStore('conflicts', { keyPath: 'id' });
      conflictStore.createIndex('resourceId', 'resourceId', { unique: false });
      conflictStore.createIndex('timestamp', 'timestamp', { unique: false });
      conflictStore.createIndex('status', 'status', { unique: false });
      conflictStore.createIndex('resourceType', 'resourceType', { unique: false });
    }

    // Cache metadata store
    if (!db.objectStoreNames.contains('cache_metadata')) {
      const cacheStore = db.createObjectStore('cache_metadata', { keyPath: 'key' });
      cacheStore.createIndex('cachedAt', 'cachedAt', { unique: false });
      cacheStore.createIndex('expiresAt', 'expiresAt', { unique: false });
      cacheStore.createIndex('size', 'size', { unique: false });
    }

    // Auth store for tokens
    if (!db.objectStoreNames.contains('auth')) {
      db.createObjectStore('auth', { keyPath: 'key' });
    }

    // Settings store
    if (!db.objectStoreNames.contains('settings')) {
      db.createObjectStore('settings', { keyPath: 'key' });
    }
  }

  // Knowledge Node Operations
  async storeKnowledgeNode(node: KnowledgeNode): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['knowledge', 'cache_metadata'], 'readwrite');
    const knowledgeStore = transaction.objectStore('knowledge');
    const metadataStore = transaction.objectStore('cache_metadata');
    
    const nodeWithMetadata = {
      ...node,
      cachedAt: new Date().toISOString(),
      offlineModified: false,
    };
    
    await this.promisifyRequest(knowledgeStore.put(nodeWithMetadata));
    
    // Update cache metadata
    const metadata: CacheMetadata = {
      key: `knowledge:${node.id}`,
      cachedAt: new Date().toISOString(),
      size: JSON.stringify(node).length,
      accessCount: 1,
      lastAccessed: new Date().toISOString(),
    };
    
    await this.promisifyRequest(metadataStore.put(metadata));
  }

  async getKnowledgeNode(id: string): Promise<KnowledgeNode | null> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['knowledge', 'cache_metadata'], 'readwrite');
    const knowledgeStore = transaction.objectStore('knowledge');
    const metadataStore = transaction.objectStore('cache_metadata');
    
    const result = await this.promisifyRequest(knowledgeStore.get(id));
    
    if (result) {
      // Update access metadata
      const metadataKey = `knowledge:${id}`;
      const metadata = await this.promisifyRequest(metadataStore.get(metadataKey));
      if (metadata) {
        metadata.accessCount++;
        metadata.lastAccessed = new Date().toISOString();
        await this.promisifyRequest(metadataStore.put(metadata));
      }
    }
    
    return result || null;
  }

  async queryKnowledgeNodes(filters: OfflineQueryFilters): Promise<KnowledgeNode[]> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['knowledge'], 'readonly');
    const store = transaction.objectStore('knowledge');
    
    let cursor: IDBRequest<IDBCursorWithValue | null>;
    
    // Determine which index to use based on filters
    if (filters.type) {
      const index = store.index('type');
      cursor = index.openCursor(IDBKeyRange.only(filters.type));
    } else if (filters.dateRange) {
      const index = store.index('lastModified');
      cursor = index.openCursor(IDBKeyRange.bound(
        filters.dateRange.start,
        filters.dateRange.end
      ));
    } else if (filters.userId) {
      const index = store.index('userId');
      cursor = index.openCursor(IDBKeyRange.only(filters.userId));
    } else {
      cursor = store.openCursor();
    }

    const results: KnowledgeNode[] = [];
    const limit = filters.limit || Infinity;
    const offset = filters.offset || 0;
    let count = 0;
    let skipped = 0;

    return new Promise((resolve, reject) => {
      cursor.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor && count < limit) {
          if (this.matchesFilters(cursor.value, filters)) {
            if (skipped >= offset) {
              results.push(cursor.value);
              count++;
            } else {
              skipped++;
            }
          }
          cursor.continue();
        } else {
          resolve(results);
        }
      };
      cursor.onerror = () => reject(cursor.error);
    });
  }

  async updateKnowledgeNodeOffline(id: string, updates: Partial<KnowledgeNode>): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['knowledge', 'sync_queue'], 'readwrite');
    const knowledgeStore = transaction.objectStore('knowledge');
    const syncStore = transaction.objectStore('sync_queue');
    
    // Get current node
    const currentNode = await this.promisifyRequest(knowledgeStore.get(id));
    if (!currentNode) {
      throw new Error('Knowledge node not found');
    }
    
    // Update node
    const updatedNode = {
      ...currentNode,
      ...updates,
      lastModified: new Date().toISOString(),
      offlineModified: true,
    };
    
    await this.promisifyRequest(knowledgeStore.put(updatedNode));
    
    // Add to sync queue
    const syncOperation: SyncOperation = {
      id: `sync-${Date.now()}-${Math.random()}`,
      type: 'update',
      resourceType: 'knowledge',
      resourceId: id,
      data: updatedNode,
      previousData: currentNode,
      timestamp: new Date().toISOString(),
      priority: 5,
      retryCount: 0,
      status: 'pending',
    };
    
    await this.promisifyRequest(syncStore.put(syncOperation));
  }

  // Conversation Operations
  async storeConversation(conversation: Conversation): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['conversations'], 'readwrite');
    const store = transaction.objectStore('conversations');
    
    const conversationWithMetadata = {
      ...conversation,
      cachedAt: new Date().toISOString(),
      offlineModified: false,
    };
    
    await this.promisifyRequest(store.put(conversationWithMetadata));
  }

  async getConversation(id: string): Promise<Conversation | null> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['conversations'], 'readonly');
    const store = transaction.objectStore('conversations');
    const result = await this.promisifyRequest(store.get(id));
    return result || null;
  }

  async storeMessage(message: Message): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['messages'], 'readwrite');
    const store = transaction.objectStore('messages');
    await this.promisifyRequest(store.put(message));
  }

  async getConversationMessages(conversationId: string, limit = 50): Promise<Message[]> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['messages'], 'readonly');
    const store = transaction.objectStore('messages');
    const index = store.index('conversationId');
    
    const messages: Message[] = [];
    const cursor = index.openCursor(IDBKeyRange.only(conversationId), 'prev');
    
    return new Promise((resolve, reject) => {
      cursor.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor && messages.length < limit) {
          messages.push(cursor.value);
          cursor.continue();
        } else {
          resolve(messages.reverse());
        }
      };
      cursor.onerror = () => reject(cursor.error);
    });
  }

  // Sync Queue Operations
  async storeSyncOperation(operation: SyncOperation): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['sync_queue'], 'readwrite');
    const store = transaction.objectStore('sync_queue');
    await this.promisifyRequest(store.put(operation));
  }

  async getPendingSyncOperations(): Promise<SyncOperation[]> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['sync_queue'], 'readonly');
    const store = transaction.objectStore('sync_queue');
    const index = store.index('status');
    
    const operations: SyncOperation[] = [];
    const cursor = index.openCursor(IDBKeyRange.only('pending'));
    
    return new Promise((resolve, reject) => {
      cursor.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          operations.push(cursor.value);
          cursor.continue();
        } else {
          resolve(operations);
        }
      };
      cursor.onerror = () => reject(cursor.error);
    });
  }

  async getPendingSyncOperationsCount(): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['sync_queue'], 'readonly');
    const store = transaction.objectStore('sync_queue');
    const index = store.index('status');
    const request = index.count(IDBKeyRange.only('pending'));
    
    return this.promisifyRequest(request);
  }

  async updateSyncOperationStatus(id: string, status: SyncOperation['status'], errorMessage?: string): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['sync_queue'], 'readwrite');
    const store = transaction.objectStore('sync_queue');
    
    const operation = await this.promisifyRequest(store.get(id));
    if (operation) {
      operation.status = status;
      if (errorMessage) {
        operation.errorMessage = errorMessage;
      }
      await this.promisifyRequest(store.put(operation));
    }
  }

  // Conflict Operations
  async storeConflict(conflict: ConflictRecord): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['conflicts'], 'readwrite');
    const store = transaction.objectStore('conflicts');
    await this.promisifyRequest(store.put(conflict));
  }

  async getConflicts(status?: ConflictRecord['status']): Promise<ConflictRecord[]> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['conflicts'], 'readonly');
    const store = transaction.objectStore('conflicts');
    
    if (status) {
      const index = store.index('status');
      return this.promisifyRequest(index.getAll(status));
    }
    
    return this.promisifyRequest(store.getAll());
  }

  async getConflictsCount(): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['conflicts'], 'readonly');
    const store = transaction.objectStore('conflicts');
    const index = store.index('status');
    const request = index.count(IDBKeyRange.only('pending'));
    
    return this.promisifyRequest(request);
  }

  async updateConflictResolution(id: string, resolution: ConflictResolution): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['conflicts'], 'readwrite');
    const store = transaction.objectStore('conflicts');
    
    const conflict = await this.promisifyRequest(store.get(id));
    if (conflict) {
      conflict.resolution = resolution;
      conflict.status = 'resolved';
      await this.promisifyRequest(store.put(conflict));
    }
  }

  // Cache Management
  async getCacheSize(): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');
    
    const stores = ['knowledge', 'conversations', 'messages', 'users'];
    let totalSize = 0;
    
    for (const storeName of stores) {
      const transaction = this.db.transaction([storeName], 'readonly');
      const store = transaction.objectStore(storeName);
      const allData = await this.promisifyRequest(store.getAll());
      
      totalSize += allData.reduce((sum, item) => {
        return sum + JSON.stringify(item).length;
      }, 0);
    }
    
    return totalSize;
  }

  async clearOldCache(maxAge: number): Promise<number> {
    if (!this.db) throw new Error('Database not initialized');
    
    const cutoffDate = new Date(Date.now() - maxAge).toISOString();
    let deletedCount = 0;
    
    // Clear old knowledge nodes
    const knowledgeTx = this.db.transaction(['knowledge'], 'readwrite');
    const knowledgeStore = knowledgeTx.objectStore('knowledge');
    const knowledgeIndex = knowledgeStore.index('lastModified');
    const knowledgeCursor = knowledgeIndex.openCursor(IDBKeyRange.upperBound(cutoffDate));
    
    await new Promise<void>((resolve, reject) => {
      knowledgeCursor.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          // Only delete if not modified offline
          if (!cursor.value.offlineModified) {
            cursor.delete();
            deletedCount++;
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      knowledgeCursor.onerror = () => reject(knowledgeCursor.error);
    });
    
    return deletedCount;
  }

  // Auth Operations
  async storeAuthToken(token: string, user: User): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['auth'], 'readwrite');
    const store = transaction.objectStore('auth');
    
    await this.promisifyRequest(store.put({
      key: 'currentUser',
      token,
      user,
      storedAt: new Date().toISOString(),
    }));
  }

  async getAuthToken(): Promise<{ token: string; user: User } | null> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['auth'], 'readonly');
    const store = transaction.objectStore('auth');
    const result = await this.promisifyRequest(store.get('currentUser'));
    
    if (result) {
      return {
        token: result.token,
        user: result.user,
      };
    }
    
    return null;
  }

  async clearAuthToken(): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['auth'], 'readwrite');
    const store = transaction.objectStore('auth');
    await this.promisifyRequest(store.delete('currentUser'));
  }

  // Settings Operations
  async storeSetting(key: string, value: any): Promise<void> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['settings'], 'readwrite');
    const store = transaction.objectStore('settings');
    
    await this.promisifyRequest(store.put({
      key,
      value,
      updatedAt: new Date().toISOString(),
    }));
  }

  async getSetting(key: string): Promise<any> {
    if (!this.db) throw new Error('Database not initialized');
    
    const transaction = this.db.transaction(['settings'], 'readonly');
    const store = transaction.objectStore('settings');
    const result = await this.promisifyRequest(store.get(key));
    
    return result?.value;
  }

  // Helper Methods
  private matchesFilters(item: any, filters: OfflineQueryFilters): boolean {
    if (filters.type && item.type !== filters.type) {
      return false;
    }
    
    if (filters.userId && item.userId !== filters.userId) {
      return false;
    }
    
    if (filters.organizationId && item.organizationId !== filters.organizationId) {
      return false;
    }
    
    if (filters.tags && filters.tags.length > 0) {
      const itemTags = item.tags || [];
      const hasAllTags = filters.tags.every(tag => itemTags.includes(tag));
      if (!hasAllTags) {
        return false;
      }
    }
    
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableFields = [
        item.title,
        item.content,
        item.description,
        ...(item.tags || []),
      ].filter(Boolean);
      
      const matches = searchableFields.some(field => 
        field.toLowerCase().includes(searchLower)
      );
      
      if (!matches) {
        return false;
      }
    }
    
    return true;
  }

  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async close(): Promise<void> {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }
}